import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Images, SlidersHorizontal, Lock } from 'lucide-react';
import type { AssetCard } from '@/lib/api/types';
import { defaultSort, useBoard, useBoardAssets, useBoardAssetsR1, useBoardChildren, useBoardFacetsR1, useBoardViews, useMembers, type BoardView } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useGuestMode } from '@/features/board-guests/useGuestMode'; // release3/F
import { DENSITY_WIDTH, useBoardViewPrefs, useGridPrefs, widthToDensity, type ViewDensity, type ViewGroupBy } from '@/stores/gridPrefs';
import { useTidyPrefs } from '@/stores/gridPrefs';
import { BoardTidyActions } from '@/features/pins/BoardTidyActions';
import { useSelection } from '@/stores/selection';
import { EmptyState, Spinner } from '@/components/Misc';
import { Button } from '@/components/Button';
import { MasonryGrid } from '@/features/grid/MasonryGrid';
import { toGridItems, isPlaceholder, type GridItem } from '@/features/grid/types';
import { BulkToolbar } from '@/features/grid/BulkToolbar';
import { GridContextMenu } from '@/features/grid/GridContextMenu';
import { QuickLook } from '@/features/grid/QuickLook';
import { FilterBar } from '@/features/grid/FilterBar';
import { useAssetActions } from '@/features/grid/useAssetActions';
import { DropZone } from '@/features/upload/DropZone';
import { uploadManager } from '@/features/upload/UploadManager';
import { useRoom } from '@/lib/ws/useRoom';
import { boardRoom } from '@/lib/shared';
import { isApiError } from '@/lib/api/errors';
import { plural } from '@/lib/format';
import { EMPTY_BOARD_FILTERS, filtersQueryParam, type BoardFilters } from '@/features/search/predicates/serialize';
import { SubBoardRow } from './SubBoardRow';
import { AssetListView } from './AssetListView';
import { DisplayMenu, ViewSwitcher } from './ViewSwitcher';
import { filterParams, groupCards, resolveViewState, viewParams, type ViewTuple } from './viewState';
import { usePermission } from '@/features/workspace/usePermission';
import { BoardFieldsEntry, BoardGuestsEntry } from './BoardDialogs'; // release2/B, release3/D
import { BoardDefaultsEntry } from './BoardDialogs'; // release3/B
// --- release3/D ---
import { isPrivateBoard } from '@/features/board-guests/visibility';
// --- release3/A ---
import { FollowButton } from '@/features/board-activity/FollowButton';
import { ActivityPanel } from '@/features/board-activity/ActivityPanel';
import { Activity } from 'lucide-react';

function StaticBoardPage() {
  const { ws = '', boardId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const activityOpen = params.get('activity') === '1'; // release3/A
  const prefs = useGridPrefs();
  const viewPrefs = useBoardViewPrefs();
  const filter = params.get('filter') === 'review' ? 'review' : 'all';
  const includeDescendants = params.get('deep') === '1';
  const board = useBoard(boardId);
  const children = useBoardChildren(boardId);
  const views = useBoardViews(boardId);
  const remember = useSessionStore((s) => s.rememberBoardMask);
  const wsId = useSessionStore((s) => s.workspace?.id);
  const guestMode = useGuestMode(); // release3/F: the member directory is 403 for guests
  const members = useMembers(guestMode ? undefined : wsId);
  useEffect(() => {
    if (board.data) remember(board.data.id, board.data.effective_mask == null ? undefined : Number(board.data.effective_mask));
  }, [board.data, remember]);
  useRoom(boardRoom(boardId));

  // --- Release 1 F1/F2: filters + saved view resolution (URL > view > device prefs) ------------------
  const boardPref = viewPrefs.byBoard[boardId];
  const fallback = useMemo<ViewTuple>(
    () => ({ filters: { ...EMPTY_BOARD_FILTERS }, sort: defaultSort(prefs.sortByBoard[boardId]), layout: prefs.view, density: boardPref?.density ?? widthToDensity(prefs.targetCardWidth), showTitles: prefs.showTitles, groupBy: boardPref?.groupBy ?? null }),
    [prefs.sortByBoard, prefs.view, prefs.targetCardWidth, prefs.showTitles, boardId, boardPref?.density, boardPref?.groupBy],
  );
  const vParam = params.get('v');
  const override = vParam ? viewPrefs.overrides[`${boardId}:${vParam}`] : undefined;
  const resolved = useMemo(() => resolveViewState(params, views.data, fallback, override), [params, views.data, fallback, override]);
  const { view: activeView, state, dirty } = resolved;
  // the bar opens itself while filters are active (shared links, saved views); an explicit toggle wins afterwards
  const [filtersToggled, setFiltersOpen] = useState<boolean | null>(null);
  const filtersOpen = filtersToggled ?? countActive(state.filters) > 0;
  // make an implicitly applied default view explicit in the URL so the top-bar sort/layout controls agree
  useEffect(() => {
    if (resolved.implicit && activeView) setParams(viewParams(params, activeView), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved.implicit, activeView?.id]);
  // top-bar card-size slider / titles toggle while a view is active become device-local overrides on that view
  const firstPrefs = useRef(true);
  useEffect(() => {
    if (firstPrefs.current) {
      firstPrefs.current = false;
      return;
    }
    if (activeView) viewPrefs.setOverride(boardId, activeView.id, { density: widthToDensity(prefs.targetCardWidth) });
    else if (boardPref?.density) viewPrefs.setBoardPref(boardId, { density: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.targetCardWidth]);
  const firstTitles = useRef(true);
  useEffect(() => {
    if (firstTitles.current) {
      firstTitles.current = false;
      return;
    }
    if (activeView) viewPrefs.setOverride(boardId, activeView.id, { showTitles: prefs.showTitles });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs.showTitles]);

  const onFilters = useCallback((f: BoardFilters) => setParams(filterParams(params, f, activeView), { replace: true }), [params, setParams, activeView]);
  const onSelectView = useCallback(
    (v: BoardView | null) => {
      if (v) viewPrefs.clearOverride(boardId, v.id);
      setParams(viewParams(params, v));
    },
    [params, setParams, viewPrefs, boardId],
  );
  const onDensity = (d: ViewDensity) => (activeView ? viewPrefs.setOverride(boardId, activeView.id, { density: d }) : viewPrefs.setBoardPref(boardId, { density: d }));
  const onGroupBy = (g: ViewGroupBy | null) => (activeView ? viewPrefs.setOverride(boardId, activeView.id, { groupBy: g }) : viewPrefs.setBoardPref(boardId, { groupBy: g }));

  const filtersParam = filtersQueryParam(state.filters);
  const sort = state.sort;
  // unfiltered boards keep the legacy listing (identical request and paging); filters route through the spine
  // B's "Show archived" (S7a): `include_archived` also routes through the spine on the API, so it joins the filters here
  const showArchived = useTidyPrefs((s) => s.showArchived);
  const useSpine = filtersParam !== undefined || showArchived;
  const legacy = useBoardAssets(boardId, { sort, includeDescendants, filter }, !useSpine);
  const spine = useBoardAssetsR1(boardId, { sort, includeDescendants, filter, filters: filtersParam, includeArchived: showArchived }, useSpine);
  const assets = useSpine ? spine : legacy;
  const facets = useBoardFacetsR1(boardId, { filters: filtersParam, includeDescendants, includeArchived: showArchived }, filtersOpen);
  const cards = useMemo(() => assets.data?.pages.flatMap((p) => p.items) ?? [], [assets.data]);
  const items = useMemo(() => toGridItems(cards), [cards]);
  const total = assets.data?.pages[0]?.total ?? null;
  const canUpload = usePermission('UPLOAD', { boardId });
  const memberName = useCallback((id: string | null | undefined) => members.data?.find((m) => m.id === id)?.user.display_name ?? 'Unknown', [members.data]);
  const groups = useMemo(() => groupCards(cards, state.groupBy, memberName), [cards, state.groupBy, memberName]);
  const cardWidth = activeView || boardPref?.density ? DENSITY_WIDTH[state.density] : undefined;

  const { run, dialogs } = useAssetActions(boardId);
  const [ctx, setCtx] = useState<{ at: { x: number; y: number }; card: AssetCard } | null>(null);
  const [quick, setQuick] = useState<AssetCard | null>(null);
  const clearSelection = useSelection((s) => s.clear);
  useEffect(() => () => clearSelection(), [boardId, clearSelection]);

  const open = useCallback(
    (item: GridItem) => {
      if (isPlaceholder(item.card)) return;
      navigate(`/w/${ws}/a/${item.card.id}`, { state: { background: location.pathname + location.search } });
    },
    [navigate, ws, location.pathname, location.search],
  );
  const onContext = useCallback((at: { x: number; y: number }, item: GridItem) => {
    if (!isPlaceholder(item.card)) setCtx({ at, card: item.card });
  }, []);
  const onQuickLook = useCallback((item: GridItem | null) => setQuick(item && !isPlaceholder(item.card) ? item.card : null), []);
  const onRetry = useCallback((id: string) => uploadManager.retry(id), []);
  const onDismiss = useCallback((id: string) => uploadManager.dismiss(id), []);
  const fetchNext = useCallback(() => void assets.fetchNextPage(), [assets]);

  if (board.isError) {
    const e = board.error;
    return <EmptyState title={isApiError(e) && e.status === 404 ? 'Board not found' : isApiError(e) && e.status === 403 ? 'You do not have access to this board' : 'Could not load board'} body={isApiError(e) ? e.message : undefined} />;
  }

  const nActive = countActive(state.filters);
  const header = (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      <h1 className="text-lg font-semibold">{board.data?.name ?? ' '}</h1>
      {board.data && isPrivateBoard(board.data) && <Lock size={14} className="text-fg-subtle" aria-label="Private board" data-private-board />} {/* release3/D */}
      {board.data && <BoardTidyActions board={board.data} />}
      {total != null && <span className="text-sm text-fg-subtle">{plural(total, 'asset', 'assets')}</span>}
      {board.data?.description && <span className="truncate text-sm text-fg-muted">— {board.data.description}</span>}
      <span className="ml-auto flex items-center gap-1.5">
        <Button size="sm" variant={filtersOpen ? 'secondary' : 'ghost'} onClick={() => setFiltersOpen(!filtersOpen)} aria-pressed={filtersOpen} aria-controls="board-filter-bar" data-toggle-filters>
          <SlidersHorizontal size={13} /> Filters{nActive ? ` (${nActive})` : ''}
        </Button>
        <DisplayMenu density={state.density} groupBy={state.groupBy} onDensity={onDensity} onGroupBy={onGroupBy} />
        <ViewSwitcher boardId={boardId} active={activeView} dirty={dirty} tuple={state} onSelect={onSelectView} />
        {board.data && <BoardFieldsEntry board={board.data} /> /* release2/B */}
        {board.data && <BoardDefaultsEntry board={board.data} /> /* release3/B */}
        {board.data && <BoardGuestsEntry board={board.data} /> /* release3/D */}
        {board.data && <FollowButton board={board.data} /> /* release3/A */}
        {/* release3/A */}
        <Button size="sm" variant={activityOpen ? 'secondary' : 'ghost'} aria-pressed={activityOpen} aria-controls="board-activity-panel" data-toggle-activity onClick={() => setParams((p) => { const n = new URLSearchParams(p); if (activityOpen) n.delete('activity'); else n.set('activity', '1'); return n; }, { replace: true })}>
          <Activity size={13} /> Activity
        </Button>
      </span>
    </div>
  );

  const gridProps = {
    onOpen: open,
    onQuickLook,
    onContextMenu: onContext,
    onRetryPlaceholder: onRetry,
    onDismissPlaceholder: onDismiss,
    sourceBoardId: boardId,
    targetCardWidth: cardWidth,
    showTitles: activeView ? state.showTitles : undefined,
  };
  const emptyState = nActive ? <EmptyState icon={<Images size={28} />} title="Nothing matches these filters" body="Try removing a filter." action={<Button size="sm" onClick={() => onFilters({ ...EMPTY_BOARD_FILTERS })}>Clear filters</Button>} /> : <EmptyState icon={<Images size={28} />} title="No assets yet" body={canUpload ? 'Drop files or folders anywhere on this page to upload.' : undefined} />;
  const footer = assets.isFetchingNextPage ? <div className="flex justify-center py-4 text-fg-muted"><Spinner /></div> : null;

  return (
    <DropZone boardId={boardId} enabled={canUpload}>
      <div className="flex min-h-full">{/* release3/A: activity side panel */}
      <div className="min-w-0 flex-1 p-4 md:p-6">
        {header}
        {filtersOpen && (
          <div id="board-filter-bar">
            <FilterBar filters={state.filters} onChange={onFilters} facets={facets.data ?? undefined} total={total} loading={assets.isFetching && !assets.isFetchingNextPage} />
          </div>
        )}
        {board.data && children.data && <SubBoardRow parent={board.data} boards={children.data} />}
        {assets.isPending ? (
          <div className="flex justify-center py-16 text-fg-muted">
            <Spinner size={20} />
          </div>
        ) : state.layout === 'list' ? (
          <AssetListView cards={cards} onOpen={(c) => open({ key: c.id, aspect: 1, card: c })} hasNextPage={assets.hasNextPage} fetchNextPage={fetchNext} />
        ) : groups ? (
          <div className="space-y-6" data-grouped={state.groupBy}>
            {groups.length === 0 && emptyState}
            {groups.map((g, i) => {
              const last = i === groups.length - 1;
              return (
                <section key={g.key} aria-labelledby={`group-${g.key}`}>
                  <h2 id={`group-${g.key}`} className="mb-2 text-sm font-semibold text-fg-muted">
                    {g.label} <span className="font-normal text-fg-subtle">{g.cards.length}</span>
                  </h2>
                  <MasonryGrid {...gridProps} items={toGridItems(g.cards)} label={`${g.label} assets`} hasNextPage={last ? assets.hasNextPage : false} isFetchingNextPage={last ? assets.isFetchingNextPage : false} fetchNextPage={last ? fetchNext : undefined} footer={last ? footer : null} />
                </section>
              );
            })}
          </div>
        ) : (
          <MasonryGrid {...gridProps} items={items} hasNextPage={assets.hasNextPage} isFetchingNextPage={assets.isFetchingNextPage} fetchNextPage={fetchNext} label={`${board.data?.name ?? 'Board'} assets`} emptyState={emptyState} footer={footer} />
        )}
      </div>
      {activityOpen && <div id="board-activity-panel" className="sticky top-0 hidden h-[calc(100vh-3rem)] md:block"><ActivityPanel boardId={boardId} ws={ws} onClose={() => setParams((p) => { const n = new URLSearchParams(p); n.delete('activity'); return n; }, { replace: true })} /></div>} {/* release3/A */}
      </div>
      <BulkToolbar boardId={boardId} run={run} />
      {ctx && <GridContextMenu at={ctx.at} card={ctx.card} boardId={boardId} onClose={() => setCtx(null)} run={run} />}
      {quick && <QuickLook card={quick} onClose={() => setQuick(null)} onOpen={() => (setQuick(null), open({ key: quick.id, aspect: 1, card: quick }))} />}
      {dialogs}
    </DropZone>
  );
}

function countActive(f: BoardFilters): number {
  let n = 0;
  for (const [k, v] of Object.entries(f)) {
    if (k === 'tag_match' || k === 'include_archived') continue;
    if (v == null || v === false || (Array.isArray(v) && v.length === 0)) continue;
    if (k === 'date' && !(v as { from?: string | null }).from && !(v as { to?: string | null }).to) continue;
    n += Array.isArray(v) ? v.length : 1;
  }
  return n;
}

// --- release2/A --- smart boards (S3) get the read-only virtual board (no upload, drop or manual order)
import { SmartBoardPage } from '@/features/smart-boards/SmartBoardPage';

/** Static boards keep the page above; it also renders while the board row loads (a smart one then swaps in). */
export function BoardPage() {
  const { boardId = '' } = useParams();
  const board = useBoard(boardId);
  if (board.data?.kind === 'smart') return <SmartBoardPage key={board.data.id} board={board.data} />;
  return <StaticBoardPage />;
}
